import axios from 'axios';
import camelcaseKeys from 'camelcase-keys';
import snakecaseKeys from 'snakecase-keys';

import {
  AxiosAccessTokenClientEhrm,
  AxiosClient,
  fetchAccount,
} from '@hcs/http-clients';
import {
  Account,
  CheckInvitationCodeResponse,
  ConfirmUserPayload,
  ForgotPasswordPayload,
  JoinOrgPayload,
  LoginPayload,
  Organization,
  OrganizationWithApplications,
  ResendConfirmUserPayload,
  ResetPasswordPayload,
  RolesResponse,
  SignUpPayload,
  UserHmacHashResponse,
} from '@hcs/types';
import { AUTH_URL, EHRMANTRAUT_URL } from '@hcs/urls';
const EMAIL_CONFIRMATION_NEEDED_STATUS = 403;
export class LoginNeedsEmailConfirmationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'LoginNeedsEmailConfirmationError';
  }
}

// ehrmantraut will respond with a 300 (which Axios considers an error code) if the user is successfully confirmed BUT are signed up for multiple orgs
const MULTIPLE_ORGS_HTTP_STATUS = 300;
export class ConfirmUserMultiOrgError extends Error {
  // base constructor only accepts string message as an argument
  // we extend it here to accept an object, allowing us to pass other data
  constructor(public account: Account, message: string) {
    super(message);
    this.name = 'ConfirmUserMultiOrgError';
    this.account = account; // this property is defined in parent
  }
}

type TokenExpiredErrorMessage = 'expired';
const CONFIRM_USER_TOKEN_EXPIRED_MESSAGE: TokenExpiredErrorMessage = 'expired';
export class ConfirmUserExpiredTokenError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ConfirmUserExpiredTokenError';
  }
}

const CONFIRM_USER_UNAUTHORIZED_HTTP_STATUS = 401;
export class ConfirmUserInvalidTokenError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ConfirmUserInvalidTokenError';
  }
}

export const AuthApi = {
  login: async ({ email, password }: LoginPayload) => {
    try {
      const response = await AxiosClient.post(`${AUTH_URL}/login`, undefined, {
        // Use Basic Auth to Send Credentials
        // Refresh token is then set in the header and used for the other endpoints in this api
        auth: {
          username: email,
          password,
        },
        withCredentials: true,
      });
      return camelcaseKeys(response.data, { deep: true });
    } catch (e) {
      if (axios.isAxiosError(e)) {
        // the response from ehrmantraut can take various forms :(
        if (
          e.response?.status === EMAIL_CONFIRMATION_NEEDED_STATUS &&
          e.response?.data.status
        ) {
          throw new LoginNeedsEmailConfirmationError(e.response.data.status);
        }
        if (e.response?.data.message) {
          throw new Error(e.response.data.message);
        }
      }
      throw e;
    }
  },
  // Uses refresh token cookie
  logout: async () => {
    await AxiosClient.post<void>(`${AUTH_URL}/logout`, null, {
      withCredentials: true,
    });
    return;
  },
  forgotPassword: async ({ email }: ForgotPasswordPayload) => {
    const response = await AxiosClient.post(
      `${EHRMANTRAUT_URL}/auth/forgot-password`,
      {
        email,
      }
    );
    return camelcaseKeys(response.data, { deep: true });
  },
  fetchAccount: async () => {
    return await fetchAccount();
  },
  fetchCurrentOrg: async () => {
    const response =
      await AxiosAccessTokenClientEhrm.get<OrganizationWithApplications>(
        `${EHRMANTRAUT_URL}/auth/current-organization`
      );
    return camelcaseKeys(response.data, { deep: true });
  },
  selectOrg: async (newOrgId: Organization['id']) => {
    const payload = {
      organizationId: newOrgId,
    };
    const response = await AxiosAccessTokenClientEhrm.post<Account>(
      `${AUTH_URL}/select-org`,
      snakecaseKeys(payload),
      {
        withCredentials: true,
      }
    );
    return camelcaseKeys(response.data, { deep: true });
  },
  signUp: async (signUpPayload: SignUpPayload) => {
    const response = await AxiosClient.post<Account>(
      `${AUTH_URL}/register`,
      snakecaseKeys(signUpPayload, { deep: true }),
      {
        withCredentials: true,
      }
    );
    return camelcaseKeys(response.data, { deep: true });
  },
  joinOrg: async (payload: JoinOrgPayload) => {
    const response = await AxiosClient.post<Account>(
      `${AUTH_URL}/register`,
      snakecaseKeys(payload, { deep: true }),
      {
        withCredentials: true,
      }
    );
    return camelcaseKeys(response.data, { deep: true });
  },
  checkResetPasswordToken: async (token: string) => {
    const response = await AxiosClient.post<Account>(
      `${EHRMANTRAUT_URL}/auth/check-reset-password-token`,
      { token }
    );
    return camelcaseKeys(response.data, { deep: true });
  },
  resetPassword: async (payload: ResetPasswordPayload) => {
    const response = await AxiosClient.post<Account>(
      `${EHRMANTRAUT_URL}/auth/confirm-reset-password`,
      snakecaseKeys(payload, { deep: true })
    );
    return camelcaseKeys(response.data, { deep: true });
  },
  checkConfirmUserToken: async ({ token }: ConfirmUserPayload) => {
    try {
      const response = await AxiosClient.get<Account>(
        `${EHRMANTRAUT_URL}/auth/confirm/${token}`
      );
      return camelcaseKeys(response.data, { deep: true });
    } catch (e) {
      if (axios.isAxiosError(e)) {
        if (e.response && e.response.status === MULTIPLE_ORGS_HTTP_STATUS) {
          // this means the account was successfully confirmed and the ehrmantraut will respond with the user context, but there are multiple orgs
          const accountData: Account = camelcaseKeys(e.response.data, {
            deep: true,
          });
          throw new ConfirmUserMultiOrgError(accountData, 'multiple orgs');
        } else if (
          e.response &&
          e.response.data.message === CONFIRM_USER_TOKEN_EXPIRED_MESSAGE
        ) {
          throw new ConfirmUserExpiredTokenError('token expired');
        } else if (
          e.response &&
          e.response.status === CONFIRM_USER_UNAUTHORIZED_HTTP_STATUS
        ) {
          throw new ConfirmUserInvalidTokenError('token invalid');
        } else if (e.response && e.response.data.message) {
          throw new Error(e.response.data.message);
        }
      }
      throw e;
    }
  },
  fetchSystemRoles: async () => {
    const makeRoleRequest = async () => {
      const response = await AxiosAccessTokenClientEhrm.get<RolesResponse>(
        `${EHRMANTRAUT_URL}/admin/role?results_per_page=100`
      );
      return camelcaseKeys(response.data, { deep: true });
    };
    return await makeRoleRequest();
  },
  resendConfirmUserEmail: async (
    expiredConfirmationToken: string,
    confirmUrl: string
  ) => {
    const payload: ResendConfirmUserPayload = {
      confirmUrl,
    };
    await AxiosClient.post(
      `${EHRMANTRAUT_URL}/auth/request-new-confirmation-token/${expiredConfirmationToken}`,
      payload
    );
  },
  checkInvitationCode: async (invitationCode: string) => {
    const response = await AxiosClient.get<CheckInvitationCodeResponse>(
      `${EHRMANTRAUT_URL}/invitation/code/${invitationCode}`
    );
    return camelcaseKeys(response.data, { deep: true });
  },
  fetchUserHmacHash: async () => {
    const response = await AxiosAccessTokenClientEhrm.get<UserHmacHashResponse>(
      `${AUTH_URL}/user-hmac`
    );
    return camelcaseKeys(response.data);
  },
};
